import { featureButton, isFeatureButton } from './feature-button';
import {
  synchronizeFeaturesState,
  updateFeaturesState,
} from './features-state';
import { Icons } from './icons';
import * as classes from './mini-widget.css';
import { getPositionClass } from './position-mapping';
import { generateUtmLink } from './utm-link';
import {
  createShadowDomElement,
  defineShadowDomElement,
} from '../core/create-shadow-dom-element';
import { expandablePanel } from '../core/expandable-panel';
import { FEATURES } from '../core/features-mapping';
import { onFeaturesClickHandler } from '../core/on-feature-click';
import { useStatistics } from '../core/use-statistics';
import { getWidgetPosition, getNoFollowAttribute } from '../core/userway';
import {
  textColor,
  widgetGradientColor,
  miniMoveY,
} from '../core/variable.css';
import { getAbsolutePosition } from '../core/widget-position';
import {
  getTextColorVar,
  getWidgetColorVar,
  getMiniMoveYVar,
} from '../utils/assign-element-vars';
import { isMobile } from '../utils/is-mobile';
import { onElementRendered } from '../utils/on-element-rendered';

interface MiniWidgetParams {
  cssBundlePath: string;
}

const { sendStatistics } = useStatistics();

const miniWidgetElement = ({ cssBundlePath }: MiniWidgetParams) =>
  createShadowDomElement({
    stylesLink: cssBundlePath,
    render: (wrapper) => {
      const widgetPosition = getWidgetPosition();
      const position = getPositionClass(widgetPosition);
      const userwayLink = generateUtmLink();

      wrapper.innerHTML = `
     <div id='wrapper' class='${
       classes.wrapper
     } ${position}' style='${getWidgetColorVar(
       widgetGradientColor,
     )}; ${getTextColorVar(textColor)}; ${getMiniMoveYVar(miniMoveY)}'>
        <button id='toggleBtn' class='${classes.closeBtn}'>
          ${Icons.open({ classes })}
        </button>
        <div class='${classes.header}'>
            <span class='${classes.title}'>Accessibility Menu</span>
        </div>
        
        <div class='${classes.features}'>
          ${featureButton({
            classes,
            title: FEATURES.INVERT_COLORS,
            icon: Icons.invertColors({ classes }),
          })}
          ${featureButton({
            classes,
            title: FEATURES.DARK_CONTRAST,
            icon: Icons.darkContrast({ classes }),
          })}
          ${featureButton({
            classes,
            title: FEATURES.HIGHLIGHT_LINKS,
            icon: Icons.highlightLinks({ classes }),
          })}
          ${featureButton({
            classes,
            title: FEATURES.BIGGER_TEXT,
            icon: Icons.biggerText({ classes }),
          })}
          ${featureButton({
            classes,
            title: FEATURES.PAUSE_ANIMATIONS,
            icon: Icons.pauseAnimations({ classes }),
          })}
          ${featureButton({
            classes,
            title: FEATURES.LEGIBLE_FONTS,
            icon: Icons.biggerText({ classes }),
          })}
          ${
            isMobile()
              ? ''
              : featureButton({
                  classes,
                  title: FEATURES.BIG_CURSOR,
                  icon: Icons.bigCursor({ classes }),
                })
          }
          ${featureButton({
            classes,
            title: FEATURES.RESET,
            icon: Icons.reset({ classes }),
          })}
        </div>
        <span class='${classes.copy}'>
        Accessibility by 
        <a
          class='${classes.copyLnk}' 
          href='${userwayLink}' 
          target='_blank'
          ${getNoFollowAttribute()}
        >
          UserWay.org
        </a>
    </span>
      </div>`;

      const featuresWrapper =
        wrapper.querySelector<HTMLDivElement>('#wrapper')!;

      synchronizeFeaturesState(featuresWrapper);

      onFeaturesClickHandler(featuresWrapper, {
        isFeatureButton,
        onFeatureClick: (button) =>
          updateFeaturesState(featuresWrapper, button.title),
      });

      const toggleButton =
        wrapper.querySelector<HTMLButtonElement>('#toggleBtn')!;
      const panelElement = wrapper.querySelector<HTMLDivElement>('#wrapper')!;

      onElementRendered(wrapper, panelElement, () => {
        expandablePanel({
          storageKey: 'uw-mini-visible',
          actionElement: toggleButton,
          onToggle: (isVisible) => {
            const width = panelElement.offsetWidth;
            const absolutePosition = getAbsolutePosition(widgetPosition);

            panelElement.style.transition = `${absolutePosition} 0.15s ease-out 0s`;
            panelElement.style[absolutePosition] = isVisible
              ? '0px'
              : `-${width}px`;
            toggleButton.innerHTML = isVisible
              ? Icons.close({ classes })
              : Icons.open({ classes });

            !isVisible && sendStatistics();
          },
        });
      });
    },
  });

const nanoWidget = (params: MiniWidgetParams) =>
  defineShadowDomElement({
    name: 'mini-widget',
    elementConstructor: miniWidgetElement(params),
  });

// eslint-disable-next-line import/no-default-export
export default nanoWidget;
